// /p/:slug/b/:branch/apps/:app — one app of the project: the app runtime's frame, full-bleed, exactly as
// cortex's preview frames it (apps/web pages/apps/Apps.tsx without cortex's own header: that bar is
// cortex's, for sharing the app; the app has none). The shared speed dial floats over it. A loader until
// the app says it mounted (postMessage `cortex-app` ready), a failure state with Try again, and the app's
// own route mirrored in the page's query (its deep link) both ways.
import { AlertTriangle, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { DEFAULT_BRANCH, projectPath } from './paths.ts';
import { appInfo, keepRuntimeSession, runtimeSession, signOut } from './session.ts';

const LOAD_TIMEOUT_MS = 45_000;

/** The app's deep link from the page's query (`?<hash>`). */
export function deepLinkOf(search: string): string {
  return search.startsWith('?') ? decodeURIComponent(search.slice(1)) : '';
}

export function AppView() {
  const { slug = '', branch = DEFAULT_BRANCH, viewName = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const info = appInfo();
  const app = info.apps.find((a) => a.key === viewName) ?? null;
  const [session, setSession] = React.useState<'pending' | 'ok' | 'failed'>('pending');
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);

  // Signed in to the runtime (its session cookie), so the app opens signed in; each new bearer follows.
  React.useEffect(() => {
    let alive = true;
    setSession('pending');
    runtimeSession().then(
      () => alive && setSession('ok'),
      () => alive && setSession('failed'),
    );
    const stop = keepRuntimeSession();
    return () => {
      alive = false;
      stop();
    };
  }, [attempt]);

  const deepLink = deepLinkOf(location.search);
  const startLink = React.useRef<{ key: string | null; link: string }>({ key: null, link: '' });
  if (startLink.current.key !== (app?.key ?? null))
    startLink.current = { key: app?.key ?? null, link: deepLink };
  const runtimeUrl = app
    ? `${window.location.origin}/${encodeURIComponent(slug)}/${encodeURIComponent(branch)}/${encodeURIComponent(app.key)}/`
    : null;
  const src =
    runtimeUrl && session !== 'pending'
      ? `${runtimeUrl}${startLink.current.link ? `#${startLink.current.link}` : ''}`
      : null;

  React.useEffect(() => {
    setReady(false);
    setFailed(null);
    if (!src) return;
    const t = setTimeout(() => setFailed('timeout'), LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [src, attempt]);

  const lastRoute = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!runtimeUrl || !app) return;
    const origin = new URL(runtimeUrl).origin;
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== origin || e.source !== iframeRef.current?.contentWindow) return;
      const d = e.data as { source?: string; type?: string; hash?: string; message?: string };
      if (d?.source !== 'cortex-app') return;
      if (d.type === 'ready') setReady(true);
      else if (d.type === 'error') setFailed(d.message ?? 'The app failed to start');
      // The app's own Sign out (@cortex/app-sdk/client signOut): the identity provider's session ends.
      else if (d.type === 'sign-out') signOut();
      else if (d.type === 'route') {
        const hash = String(d.hash ?? '');
        if (hash === lastRoute.current) return;
        lastRoute.current = hash;
        const base = projectPath(`/apps/${encodeURIComponent(app.key)}`, slug, branch);
        navigate(base + (hash ? `?${hash}` : ''), { replace: true });
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [runtimeUrl, app?.key, slug, branch, navigate]);
  React.useEffect(() => {
    if (!runtimeUrl || !ready || deepLink === lastRoute.current) return;
    lastRoute.current = deepLink;
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { source: 'cortex-app-route', hash: deepLink },
        new URL(runtimeUrl).origin,
      );
    } catch {
      /* the frame navigated away */
    }
  }, [deepLink, runtimeUrl, ready]);

  React.useEffect(() => {
    if (app) document.title = `${app.title} · ${info.branding.name}`;
  }, [app, info.branding.name]);

  if (!app)
    return (
      <div
        className="flex h-full items-center justify-center text-sm text-page-muted"
        data-testid="app-not-found"
      >
        This app has no app named {viewName}.
      </div>
    );

  return (
    <div className="flex h-full min-h-0" data-testid="apps-page" data-surface="app">
      <main className="relative flex-1 min-w-0 bg-page-bg">
        {src ? (
          <>
            <iframe
              key={`${app.key}#${attempt}`}
              ref={iframeRef}
              data-testid="app-view-iframe"
              src={src}
              title={app.title}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
            {!ready && (
              <div
                style={{ position: 'absolute', inset: 0, zIndex: 10 }}
                className="flex flex-col items-center justify-center gap-3 bg-page-bg px-6 text-center"
                data-testid={failed ? 'app-failed' : 'app-loading'}
              >
                {failed ? (
                  <>
                    <AlertTriangle size={30} className="text-amber-600" />
                    <div className="text-base font-semibold text-page-title">
                      {failed === 'timeout' ? "This app didn't finish loading" : "You can't open this app"}
                    </div>
                    <div className="text-sm text-page-muted max-w-[460px]">
                      {failed === 'timeout'
                        ? 'It may still be starting up, or you may not have permission to run it. Try again, and contact the app’s owner if it keeps failing.'
                        : failed}
                    </div>
                    <button
                      type="button"
                      data-testid="app-try-again"
                      onClick={() => setAttempt((n) => n + 1)}
                      className="mt-1 h-9 px-4 rounded-[5px] border-0 cursor-pointer bg-nav-active text-white text-sm font-medium hover:bg-nav-active-dark transition-colors"
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  <>
                    <Loader2 size={28} className="animate-spin text-nav-active" />
                    <div className="text-sm text-page-muted">Opening {app.title}…</div>
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <div
            style={{ position: 'absolute', inset: 0 }}
            className="flex flex-col items-center justify-center gap-3 bg-page-bg"
            data-testid="app-loading"
          >
            <Loader2 size={28} className="animate-spin text-nav-active" />
            <div className="text-sm text-page-muted">Opening {app.title}…</div>
          </div>
        )}
      </main>
    </div>
  );
}
